import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { dismissAlert, getAlerts, markAllAlertsRead } from '../utils/api';
import { fmt } from '../utils/format';

function severityBadge(severity) {
  return {
    red: 'badge-red',
    amber: 'badge-amber',
    green: 'badge-green',
    blue: 'badge-blue',
  }[severity] || 'badge-gray';
}

function actionLabel(alert) {
  if (alert.action_type === 'phone') return 'Call supplier';
  if (alert.action_type === 'print') return 'Open print queue';
  return 'Open';
}

export default function AlertsPage() {
  const qc = useQueryClient();
  const { data: alerts = [], isLoading } = useQuery({
    queryKey: ['alerts'],
    queryFn: () => getAlerts().then((response) => response.data),
    refetchInterval: 30000,
  });

  const markReadMut = useMutation({
    mutationFn: () => markAllAlertsRead(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['alerts'] });
      toast.success('Alerts marked as read');
    },
    onError: (err) => toast.error(err?.response?.data?.error || 'Unable to mark alerts as read'),
  });

  const dismissMut = useMutation({
    mutationFn: (id) => dismissAlert(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['alerts'] }),
    onError: (err) => toast.error(err?.response?.data?.error || 'Unable to dismiss alert'),
  });

  return (
    <div className="p-page fade-up">
      <div className="inventory-toolbar mb-16">
        <div>
          <div className="card-title" style={{ fontSize: 18 }}>System Alerts</div>
          <div className="card-sub">Live stock and printer alerts from the running system.</div>
        </div>
        <div className="inventory-actions">
          <button className="btn btn-secondary" onClick={() => qc.invalidateQueries({ queryKey: ['alerts'] })}>Refresh</button>
          <button className="btn btn-primary" onClick={() => markReadMut.mutate()} disabled={markReadMut.isPending || !alerts.length}>Mark all read</button>
        </div>
      </div>

      <div className="grid-2">
        <div className="card card-pad">
          <div className="card-title" style={{ marginBottom: 14 }}>Active alerts</div>
          {isLoading ? (
            <div className="empty-state"><div className="spinner" style={{ margin: '0 auto' }} /></div>
          ) : alerts.length === 0 ? (
            <div className="empty-state"><div className="empty-title">No active alerts</div><div className="empty-sub">Low stock, print queue issues, and new operational alerts will show here.</div></div>
          ) : (
            <div style={{ display: 'grid', gap: 12 }}>
              {alerts.map((alert) => {
                const actionPayload = alert.action_payload || {};
                const callHref = actionPayload.phone ? `tel:${actionPayload.phone}` : null;
                return (
                  <div key={alert.id} className="card" style={{ padding: 16, borderRadius: 'var(--r)' }}>
                    <div className="flex-between mb-8" style={{ alignItems: 'flex-start' }}>
                      <div>
                        <div className="fw-700" style={{ marginBottom: 4 }}>{alert.title}</div>
                        <div className="text-sm text-muted">{alert.message}</div>
                      </div>
                      <span className={`badge ${severityBadge(alert.severity)}`}>{alert.severity}</span>
                    </div>
                    <div className="flex-between" style={{ flexWrap: 'wrap' }}>
                      <div className="text-sm text-muted">{fmt.datetime(alert.created_at || alert.updated_at)}</div>
                      <div className="inventory-row-actions">
                        {alert.action_type === 'phone' && callHref && <a className="btn btn-secondary btn-sm" href={callHref}>{actionLabel(alert)}</a>}
                        {alert.action_type === 'phone' && !callHref && <button className="btn btn-secondary btn-sm" disabled>No supplier phone</button>}
                        {alert.action_type === 'print' && <button className="btn btn-secondary btn-sm" onClick={() => toast('Check printer tools in Settings')}>{actionLabel(alert)}</button>}
                        <button className="btn btn-danger btn-sm" onClick={() => dismissMut.mutate(alert.id)} disabled={dismissMut.isPending}>Dismiss</button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="card card-pad">
          <div className="card-title" style={{ marginBottom: 14 }}>What is live now</div>
          <div style={{ display: 'grid', gap: 12 }}>
            <div className="card" style={{ padding: 16, borderRadius: 'var(--r)' }}>
              <div className="fw-700">Reorders use supplier phone numbers</div>
              <div className="text-sm text-muted mt-8">When stock drops below the threshold, the alert shows a direct call action instead of a fake email workflow.</div>
            </div>
            <div className="card" style={{ padding: 16, borderRadius: 'var(--r)' }}>
              <div className="fw-700">Printer alerts are tied to queued jobs</div>
              <div className="text-sm text-muted mt-8">Pending and failed print jobs create real alerts. Connection testing is available in Settings.</div>
            </div>
            <div className="card" style={{ padding: 16, borderRadius: 'var(--r)' }}>
              <div className="fw-700">No fake backup or sync cards</div>
              <div className="text-sm text-muted mt-8">This page now reflects actual system conditions instead of static demo messages.</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

